import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { handleAlert } from "redux/festures/alertSlice";
import http from "Utils/api";
import { useDebounceHook } from "hooks/useDebounce";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import SkLoading from "components/SkLoading";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Card from "@mui/material/Card";
import { Icon, Pagination, Stack, Select, MenuItem, TextField } from "@mui/material";
import DataTable from "examples/Tables/DataTable";
import { useMaterialUIController } from "context";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

const columns = [
  { Header: "S.No", accessor: "no", width: "5%" },
  { Header: "Partner Info", accessor: "partnerInfo", width: "20%" },
  { Header: "Booking Details", accessor: "bookingDetails", width: "15%" },
  { Header: "Amount", accessor: "amount", width: "10%" },
  { Header: "Commission", accessor: "commission", width: "15%" },
  { Header: "Status", accessor: "status", width: "8%" },
  { Header: "Date", accessor: "date", width: "12%" },
  { Header: "Description", accessor: "description", width: "15%" },
];

const Commissions = () => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const dispatch = useDispatch();
  const adminId = localStorage.getItem("admin_id");

  const [commissions, setCommissions] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 50,
    pages: 1,
  });

  // Filter states
  const [searchKey, setSearchKey] = useState("");
  const [selectedPartnerId, setSelectedPartnerId] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const ITEMS_PER_PAGE = 50;
  
  // Debounce search input to prevent multiple API calls
  const debouncedSearchKey = useDebounceHook(searchKey, 500);

  const fetchCommissions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        ...(debouncedSearchKey && { key: debouncedSearchKey }),
        ...(selectedPartnerId && { partnerId: selectedPartnerId }),
        ...(selectedStatus && { status: selectedStatus }),
        ...(startDate && { startDate: startDate.toISOString().split('T')[0] }),
        ...(endDate && { endDate: endDate.toISOString().split('T')[0] }),
      });

      const response = await http.get(`/commissions?${params.toString()}`);
      
      if (response.data.success) {
        setCommissions(response.data.data);
        setSummary(response.data.summary || {});
        setPagination(response.data.pagination);
      } else {
        dispatch(
          handleAlert({
            isOpen: true,
            type: "error",
            msg: response.data.message || "Failed to fetch commissions",
          })
        );
      }
    } catch (error) {
      dispatch(
        handleAlert({
          isOpen: true,
          type: "error",
          msg: error.response?.data?.message || "Error fetching commissions",
        })
      );
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearchKey, selectedPartnerId, selectedStatus, startDate, endDate]);

  useEffect(() => {
    fetchCommissions();
  }, [fetchCommissions]);

  const handleSearch = (value) => {
    setSearchKey(value);
    setCurrentPage(1);
  };

  const handleFilterChange = (setter) => (value) => {
    setter(value);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchKey("");
    setSelectedPartnerId("");
    setSelectedStatus("");
    setStartDate(null);
    setEndDate(null);
    setCurrentPage(1);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED':
        return 'success';
      case 'CANCELLED':
        return 'error';
      case 'PENDING':
        return 'warning';
      default:
        return 'info';
    }
  };

  const rowsData = commissions.map((commission, index) => ({
    no: (
      <MDTypography sx={{ fontSize: 12, fontWeight: "medium" }} variant="text">
        {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
      </MDTypography>
    ),
    partnerInfo: (
      <MDBox
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        <MDTypography sx={{ fontSize: 12, fontWeight: "medium" }} variant="text">
          {commission.partnerId?.name || "N/A"}
        </MDTypography>
        <MDTypography sx={{ fontSize: 10,  }} variant="text">
          {commission.partnerId?.email || "N/A"}
        </MDTypography>
        <MDTypography sx={{ fontSize: 10 }} variant="text">
          ID: {commission.partnerId?._id}
        </MDTypography>
      </MDBox>
    ),
    bookingDetails: (
      <MDBox
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        <MDTypography sx={{ fontSize: 11 }} variant="text">
          {commission.bookingId || "N/A"}
        </MDTypography>
        <MDTypography sx={{ fontSize: 10,  }} variant="text">
          {commission.orderType || "N/A"}
        </MDTypography>
      </MDBox>
    ),
    amount: (
      <MDBox
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        <MDTypography sx={{ fontSize: 11, fontWeight: "medium" }} variant="text">
          {formatCurrency(commission.amount)}
        </MDTypography>
        <MDTypography sx={{ fontSize: 10,  }} variant="text">
          Net: {formatCurrency(commission.netAmount)}
        </MDTypography>
      </MDBox>
    ),
    commission: (
      <MDBox
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        <MDTypography sx={{ fontSize: 11, fontWeight: "medium" }} variant="text">
          {formatCurrency(commission.commissionAmount)}
        </MDTypography>
        <MDTypography sx={{ fontSize: 10, }} variant="text">
          {commission.commissionPercentage}%
        </MDTypography>
      </MDBox>
    ),
    status: (
      <MDBox
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        <MDBox
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: `${getStatusColor(commission.status)}.main`,
          }}
        />
        <MDTypography sx={{ fontSize: 10 }} variant="text">
          {commission.status}
        </MDTypography>
      </MDBox>
    ),
    date: (
      <MDTypography sx={{ fontSize: 11 }} variant="text">
        {formatDate(commission.createdAt)}
      </MDTypography>
    ),
    description: (
      <MDTypography
        sx={{ fontSize: 11 }}
        variant="text"
        style={{
          maxWidth: "200px",
          lineHeight: "14px",
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 2,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {commission.description || "N/A"}
      </MDTypography>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mb={2} />
      <MDBox py={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <MDTypography variant="h6" color="white">
              Commissions Management
            </MDTypography>
            <MDButton
              variant="gradient"
              color="dark"
              onClick={fetchCommissions}
              disabled={loading}
            >
              <Icon fontSize="small">refresh</Icon>
              &nbsp; Refresh
            </MDButton>
          </MDBox>

          {/* Summary Section */}
          {Object.keys(summary).length > 0 && (
            <MDBox px={3} py={2}>
              <MDBox
                sx={{
                  display: "flex",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                {Object.entries(summary).map(([status, data]) => (
                  <MDBox
                    key={status}
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      backgroundColor: darkMode ? "grey.800" : "grey.100",
                      minWidth: 150,
                    }}
                  >
                    <MDTypography variant="h6" color={getStatusColor(status)}>
                      {status}
                    </MDTypography>
                    <MDTypography variant="body2">
                      Count: {data.count}
                    </MDTypography>
                    <MDTypography variant="body2" fontWeight="medium">
                      Total: {formatCurrency(data.total)}
                    </MDTypography>
                  </MDBox>
                ))}
              </MDBox>
            </MDBox>
          )}

          {/* Filters Section */}
          <MDBox px={3} py={2}>
            <MDTypography variant="h6" mb={2}>
              Filters
            </MDTypography>
            <MDBox
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                alignItems: "flex-end",
              }}
            >
              <MDBox sx={{ minWidth: 200 }}>
                <MDTypography variant="button" mb={1}>
                  Search
                </MDTypography>
                <MDInput
                  placeholder="Search by partner name/email..."
                  type="text"
                  fullWidth
                  value={searchKey}
                  onChange={(e) => handleSearch(e.target.value)}
                  disabled={loading}
                  startAdornment={
                    <Icon fontSize="small" sx={{ mr: 1, color: "text.secondary" }}>
                      search
                    </Icon>
                  }
                />
              </MDBox>

              <MDBox sx={{ minWidth: 150 }}>
                <MDTypography variant="button" mb={1}>
                  Partner ID
                </MDTypography>
                <MDInput
                  placeholder="Partner ID"
                  type="text"
                  fullWidth
                  value={selectedPartnerId}
                  onChange={(e) => handleFilterChange(setSelectedPartnerId)(e.target.value)}
                  disabled={loading}
                />
              </MDBox>

              <MDBox sx={{ minWidth: 120 }}>
                <MDTypography variant="button" mb={1}>
                  Status
                </MDTypography>
                <Select
                  value={selectedStatus}
                  onChange={(e) => handleFilterChange(setSelectedStatus)(e.target.value)}
                  disabled={loading}
                  fullWidth
                  sx={({ palette: { dark, white, info } }) => ({
                    color: darkMode ? white?.main : dark?.main,
                    height:"2.5rem",
                    bgcolor: "transparent",
                    "& .MuiSelect-icon": {
                      color: darkMode ? white?.main : dark?.main,
                    },
                  })}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="COMPLETED">Completed</MenuItem>
                  <MenuItem value="CANCELLED">Cancelled</MenuItem>
                  <MenuItem value="PENDING">Pending</MenuItem>
                </Select>
              </MDBox>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <MDBox sx={{ minWidth: 150 }}>
                  <MDTypography variant="button" mb={1}>
                    Start Date
                  </MDTypography>
                  <DatePicker
                    value={startDate}
                    onChange={(newValue) => handleFilterChange(setStartDate)(newValue)}
                    disabled={loading}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        sx: ({ palette: { dark, white, info } }) => ({
                          color: darkMode ? white?.main : dark?.main,
                          bgcolor: "transparent",
                          "& .MuiInputBase-input": {
                            color: darkMode ? white?.main : dark?.main,
                          },
                        }),
                      },
                    }}
                  />
                </MDBox>
              </LocalizationProvider>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <MDBox sx={{ minWidth: 150 }}>
                  <MDTypography variant="button" mb={1}>
                    End Date
                  </MDTypography>
                  <DatePicker
                    value={endDate}
                    onChange={(newValue) => handleFilterChange(setEndDate)(newValue)}
                    disabled={loading}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        sx: ({ palette: { dark, white, info } }) => ({
                          color: darkMode ? white?.main : dark?.main,
                          bgcolor: "transparent",
                          "& .MuiInputBase-input": {
                            color: darkMode ? white?.main : dark?.main,
                          },
                        }),
                      },
                    }}
                  />
                </MDBox>
              </LocalizationProvider>

              <MDBox sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
                <MDButton
                  variant="outlined"
                  color="info"
                  onClick={clearFilters}
                  disabled={loading}
                >
                  Clear Filters
                </MDButton>
              </MDBox>
            </MDBox>
          </MDBox>

          {/* Table Section */}
          <MDBox py={3}>
            {loading ? (
              <SkLoading />
            ) : commissions && commissions.length > 0 ? (
              <>
                <DataTable
                  table={{
                    columns: columns,
                    rows: rowsData,
                  }}
                  isSorted={false}
                  entriesPerPage={false}
                  noEndBorder
                  canSearch={false}
                  showTotalEntries={false}
                  pagination={false}
                  isPagination={false}
                />
                
                {/* Pagination */}
                {pagination.pages > 1 && (
                  <MDBox
                    sx={{
                      mt: 5,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Stack spacing={2} direction="row">
                      <Pagination
                        sx={({ palette: { dark, white, info } }) => ({
                          "& .MuiPaginationItem-text": {
                            color: darkMode ? white.main : dark.main,
                          },
                          "& .MuiPaginationItem-icon": {
                            color: darkMode ? white.main : dark.main,
                          },
                          "& .MuiPaginationItem-textInfo": {
                            color: darkMode ? white.main : dark.main,
                          },
                        })}
                        color="info"
                        variant="text"
                        count={pagination.pages}
                        page={currentPage}
                        onChange={(e, value) => setCurrentPage(value)}
                      />
                    </Stack>
                  </MDBox>
                )}
              </>
            ) : (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                py={5}
              >
                <MDTypography variant="h5" color="text.secondary">
                  {searchKey || selectedPartnerId || selectedStatus || startDate || endDate 
                    ? "No commissions found matching your filters" 
                    : "No commissions found"}
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default Commissions;
